import React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { isStatusHost, siteHref } from '@utils/appDomain';

/**
 * Navigation to landing sections (`/#features`, `/#pricing`, …) that works
 * from every page.
 *
 * Before: the header used plain `<a href="/#pricing">`. On the landing the
 * browser jumped to the anchor (hidden under the fixed header); from any
 * other page it did a full reload of `/`, and `initScrollTop()` then forced
 * the page back to the top — so the user landed "somewhere" on the home page.
 *
 * Now:
 * - on the landing: smooth-scroll to the section below the fixed header and
 *   push `#id` into the URL (no reload);
 * - on any other page: client-side navigate to `/#id`; `LandingPage` reads the
 *   hash once its sections are mounted and scrolls there instantly.
 */

export type LandingSection = 'features' | 'privacy' | 'pricing' | 'partners';

/**
 * Nav label for a landing section — the same string the section itself shows
 * as its eyebrow on the landing, so every header (landing, knowledge base,
 * app) calls the section the same thing in every language. Falls back to the
 * Russian label when the dictionary has no value yet.
 */
export function landingSectionLabel(t: (key: string) => string, section: LandingSection): string {
  const pick = (key: string, fallback: string) => {
    const v = t(key);
    return v && v !== key ? v : fallback;
  };
  switch (section) {
    case 'features':
      return pick('landing.features.title', 'Возможности');
    case 'privacy':
      return pick('landing.privacy.eyebrow', 'Приватность');
    case 'pricing':
      return pick('nav.plans', 'Тарифы');
    case 'partners':
      return pick('nav.partners', 'Партнёрам');
  }
}

/** Fixed landing header is 72px; keep a little air above the section. */
export const LANDING_HEADER_OFFSET = 84;

const INSTANT = 'instant' as ScrollBehavior;

export function isLandingPath(pathname: string): boolean {
  return pathname === '/' || pathname === '';
}

/** Scrolls the window so `#id` sits just below the fixed header. False when the section is not in the DOM. */
export function scrollToLandingSection(id: string, behavior: ScrollBehavior = 'smooth'): boolean {
  const el = document.getElementById(id);
  if (!el) return false;
  const top = Math.max(0, el.getBoundingClientRect().top + window.scrollY - LANDING_HEADER_OFFSET);
  window.scrollTo({ top, left: 0, behavior });
  return true;
}

/** Scrolls to the section named in `location.hash`, if any. */
export function scrollToLocationHash(behavior: ScrollBehavior = INSTANT): boolean {
  const raw = window.location.hash.slice(1);
  if (!raw) return false;
  let id = raw;
  try {
    id = decodeURIComponent(raw);
  } catch {
    /* keep raw */
  }
  return scrollToLandingSection(id, behavior);
}

/** Sets `#id` in the address bar without reloading or re-routing. */
function pushSectionHash(id: string): void {
  const url = `${window.location.pathname}${window.location.search}#${id}`;
  if (window.location.hash === `#${id}`) return;
  window.history.pushState(window.history.state, '', url);
}

/**
 * Delegated handler for in-page anchors inside the landing (`<a href="#cta">`):
 * smooth scroll with header offset instead of the browser's raw jump.
 */
export function initLandingAnchorLinks(root: HTMLElement): () => void {
  const onClick = (e: MouseEvent) => {
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    const target = e.target as Element | null;
    const a = target?.closest?.('a[href^="#"]') as HTMLAnchorElement | null;
    if (!a || !root.contains(a)) return;
    const id = (a.getAttribute('href') || '').slice(1);
    if (!id || !document.getElementById(id)) return;
    e.preventDefault();
    scrollToLandingSection(id, 'smooth');
    pushSectionHash(id);
  };
  const onHashChange = () => {
    // Back/forward between `#section` entries.
    scrollToLocationHash('smooth');
  };
  root.addEventListener('click', onClick);
  window.addEventListener('hashchange', onHashChange);
  return () => {
    root.removeEventListener('click', onClick);
    window.removeEventListener('hashchange', onHashChange);
  };
}

type SectionLinkProps = Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
  /** Landing section id without `#`, e.g. "pricing". */
  section: string;
  /** Runs before navigating — used to close the mobile menu. */
  onNavigate?: () => void;
};

/** `<a href="/#section">` that scrolls on the landing and routes to it from elsewhere. */
export const SectionLink: React.FC<SectionLinkProps> = ({ section, onNavigate, onClick, children, ...rest }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const localHref = `/#${section}`;
  const href = siteHref(localHref);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    onClick?.(e);
    if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
    onNavigate?.();
    if (isStatusHost()) return;
    e.preventDefault();
    if (isLandingPath(location.pathname)) {
      requestAnimationFrame(() => {
        if (scrollToLandingSection(section, 'smooth')) pushSectionHash(section);
      });
    } else {
      navigate(localHref);
    }
  };

  return <a href={href} onClick={handleClick} {...rest}>{children}</a>;
};
